<script setup lang="ts">
import type { NuxtError } from '#app';

const props = defineProps<{
  error: NuxtError;
}>();

const { t } = useI18n();
const localePath = useLocalePath();

const handleError = () => clearError({ redirect: localePath('/') });

const isProductNotFound = computed(() =>
  props.error.statusCode === 404 &&
  props.error.statusMessage === 'Product Not Found'
);

const title = computed(() => {
  if (isProductNotFound.value) return t('errors.productNotFound.title');
  if (props.error.statusCode === 404) return t('errors.notFound.title');
  return t('errors.generic.title');
});

const message = computed(() => {
  if (isProductNotFound.value) return t('errors.productNotFound.message');
  if (props.error.statusCode === 404) return t('errors.notFound.message');
  return props.error.message || t('errors.generic.message');
});
</script>

<template>
  <div class="error-page">
    <div class="error-content">
      <h1 class="error-code">{{ error.statusCode }}</h1>
      <h2 class="error-title">{{ title }}</h2>
      <p class="error-message">{{ message }}</p>
      <button class="btn-cta" @click="handleError">
        {{ t('errors.backToHome') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.error-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--lightest-blue);
  padding: 2rem;
}

.error-content {
  text-align: center;
  max-width: 500px;
}

.error-code {
  font-size: 8rem;
  font-weight: 900;
  color: var(--dark-blue);
  line-height: 1;
  margin-bottom: 1rem;
}

.error-title {
  font-size: 2rem;
  color: var(--dark-blue);
  margin-bottom: 1rem;
}

.error-message {
  font-size: 1.1rem;
  color: var(--blue);
  margin-bottom: 2rem;
}

.btn-cta {
  display: inline-block;
}
</style>
